const MAX_ITERATIONS = 255;
const ESCAPE_THRESHOLD = 4; // 2^2
const LOG2_INV = 1 / Math.log(2);

// Reusable result object to avoid allocations in hot loop
const iterResult = { iterations: 0, distanceSquared: 0 };

/**
 * Highly optimized iteration function
 * - Avoids array allocation by using reusable result object
 * - Uses manual abs() to avoid function call overhead
 * - Returns squared distance (caller computes sqrt only when needed)
 */
function iterateUntilEscape(x0, y0, result) {
  let x = 0, y = 0;
  let iteration = 0;
  let x2 = 0, y2 = 0;
  
  // Unrolled first iteration (we know x=0, y=0)
  // Skip first iteration's abs since x*y = 0
  
  while (x2 + y2 < ESCAPE_THRESHOLD && iteration < MAX_ITERATIONS) {
    // Manual abs: (x * y) >= 0 ? (x * y) : -(x * y)
    // Equivalent to: Math.abs(x * y) but faster
    const xy = x * y;
    const absXY = xy >= 0 ? xy : -xy;
    const y_n = absXY + absXY + y0; // 2 * absXY = absXY + absXY (avoids multiplication)
    x = x2 - y2 + x0;
    y = y_n;
    x2 = x * x;
    y2 = y * y;
    iteration++;
  }
  
  result.iterations = iteration;
  result.distanceSquared = x2 + y2;
  return result;
}

/**
 * Re-normalized iteration count for smoother images
 * https://mathr.co.uk/helm/AtTheHelmOfTheBurningShip-Paper.pdf
 */
function getMu(numIterations, distanceSquared) {
  // log(sqrt(x)) = 0.5 * log(x)
  const logDist = 0.5 * Math.log(distanceSquared);
  return numIterations + 1 - Math.log(logDist) * LOG2_INV;
}

// Reusable object for colorFunc to avoid allocation in hot loop
const colorInput = { numIterations: 0, mu: 0 };

/**
 * Given a canvas element, draw a fractal using the escape time method
 * Highly optimized:
 * - Precomputed scale factors
 * - Reusable objects to avoid allocations
 * - Inlined pixel writes
 */
export function drawBurningShipFractal(canvasEl, xRange, yRange, colorFunc) {
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  const context = canvasEl.getContext('2d');
  const canvasImageData = context.createImageData(canvasWidth, canvasHeight);
  const image = canvasImageData.data;
  const image32 = new Uint32Array(image.buffer);
  
  // Precompute scale factors to avoid division in inner loop
  const xScale = (xRange[1] - xRange[0]) / canvasWidth;
  const yScale = (yRange[1] - yRange[0]) / canvasHeight;
  const xBase = xRange[0];
  const yBase = yRange[0];
  
  const result = iterResult;
  
  for (let i = 0; i < canvasHeight; i++) {
    const y0 = yBase + i * yScale;
    const rowStart32 = i * canvasWidth;
    
    for (let j = 0; j < canvasWidth; j++) {
      const x0 = xBase + j * xScale;
      iterateUntilEscape(x0, y0, result);
      
      if (result.iterations !== MAX_ITERATIONS) {
        const mu = getMu(result.iterations, result.distanceSquared);
        colorInput.numIterations = result.iterations;
        colorInput.mu = mu;
        const [r, g, b, a] = colorFunc(colorInput);
        const pr = r <= 0 ? 0 : r >= 255 ? 255 : r | 0;
        const pg = g <= 0 ? 0 : g >= 255 ? 255 : g | 0;
        const pb = b <= 0 ? 0 : b >= 255 ? 255 : b | 0;
        const pa = a <= 0 ? 0 : a >= 255 ? 255 : a | 0;
        image32[rowStart32 + j] = pr | (pg << 8) | (pb << 16) | (pa << 24);
      }
    }
  }
  context.putImageData(canvasImageData, 0, 0);
}

/**
 * Check if a range change is a pure pan (same scale, different offset)
 */
export function isPanOnly(oldXRange, oldYRange, newXRange, newYRange) {
  const oldXSpan = oldXRange[1] - oldXRange[0];
  const oldYSpan = oldYRange[1] - oldYRange[0];
  const newXSpan = newXRange[1] - newXRange[0];
  const newYSpan = newYRange[1] - newYRange[0];
  
  // Check if spans are equal (within floating point tolerance)
  const tolerance = 1e-10;
  return Math.abs(oldXSpan - newXSpan) < tolerance && 
         Math.abs(oldYSpan - newYSpan) < tolerance;
}

/**
 * Apply a pan to existing canvas content and render only new edges
 * Returns true if pan was applied, false if full re-render needed
 * 
 * Optimizations:
 * - Uses Uint32Array view for bulk 4-byte pixel copying
 * - Row-based bulk copy when possible
 * - Reusable objects to avoid allocations
 */
export function applyPanAndRenderEdges(canvasEl, oldXRange, oldYRange, newXRange, newYRange, colorFunc) {
  if (!isPanOnly(oldXRange, oldYRange, newXRange, newYRange)) {
    return false;
  }
  
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  const context = canvasEl.getContext('2d');
  
  // Calculate pixel offset
  const xScale = (newXRange[1] - newXRange[0]) / canvasWidth;
  const yScale = (newYRange[1] - newYRange[0]) / canvasHeight;
  const pixelOffsetX = Math.round((newXRange[0] - oldXRange[0]) / xScale);
  const pixelOffsetY = Math.round((newYRange[0] - oldYRange[0]) / yScale);
  
  // If offset is too large (more than 80% of canvas), just re-render
  if (Math.abs(pixelOffsetX) > canvasWidth * 0.8 || 
      Math.abs(pixelOffsetY) > canvasHeight * 0.8) {
    return false;
  }
  
  // Get existing image data
  const existingData = context.getImageData(0, 0, canvasWidth, canvasHeight);
  const newImageData = context.createImageData(canvasWidth, canvasHeight);
  
  const oldPixels32 = new Uint32Array(existingData.data.buffer);
  const newPixels32 = new Uint32Array(newImageData.data.buffer);
  
  // Calculate valid copy range
  const srcStartY = Math.max(0, pixelOffsetY);
  const srcEndY = Math.min(canvasHeight, canvasHeight + pixelOffsetY);
  const dstStartY = Math.max(0, -pixelOffsetY);
  
  const srcStartX = Math.max(0, pixelOffsetX);
  const srcEndX = Math.min(canvasWidth, canvasWidth + pixelOffsetX);
  const dstStartX = Math.max(0, -pixelOffsetX);
  const copyWidth = srcEndX - srcStartX;
  
  // Bulk copy using Uint32Array (4 bytes per operation)
  if (copyWidth > 0) {
    for (let srcY = srcStartY, dstY = dstStartY; srcY < srcEndY; srcY++, dstY++) {
      const srcRowStart = srcY * canvasWidth + srcStartX;
      const dstRowStart = dstY * canvasWidth + dstStartX;
      // Copy entire row segment at once
      newPixels32.set(
        oldPixels32.subarray(srcRowStart, srcRowStart + copyWidth),
        dstRowStart
      );
    }
  }
  
  // Precompute scale factors
  const xBase = newXRange[0];
  const yBase = newYRange[0];
  
  // Local references for hot loop
  const result = iterResult;
  const cInput = colorInput;
  
  // Render newly visible horizontal strip (left or right edge)
  if (pixelOffsetX !== 0) {
    const stripStartX = pixelOffsetX > 0 ? canvasWidth - pixelOffsetX : 0;
    const stripEndX = pixelOffsetX > 0 ? canvasWidth : -pixelOffsetX;
    
    for (let y = 0; y < canvasHeight; y++) {
      const y0 = yBase + y * yScale;
      const rowOffset = y * canvasWidth;
      
      for (let x = stripStartX; x < stripEndX; x++) {
        const x0 = xBase + x * xScale;
        iterateUntilEscape(x0, y0, result);
        
        if (result.iterations !== MAX_ITERATIONS) {
          const mu = getMu(result.iterations, result.distanceSquared);
          cInput.numIterations = result.iterations;
          cInput.mu = mu;
          const [r, g, b, a] = colorFunc(cInput);
          const pr = r <= 0 ? 0 : r >= 255 ? 255 : r | 0;
          const pg = g <= 0 ? 0 : g >= 255 ? 255 : g | 0;
          const pb = b <= 0 ? 0 : b >= 255 ? 255 : b | 0;
          const pa = a <= 0 ? 0 : a >= 255 ? 255 : a | 0;
          newPixels32[rowOffset + x] = pr | (pg << 8) | (pb << 16) | (pa << 24);
        }
      }
    }
  }
  
  // Render newly visible vertical strip (top or bottom edge)
  if (pixelOffsetY !== 0) {
    const stripStartY = pixelOffsetY > 0 ? canvasHeight - pixelOffsetY : 0;
    const stripEndY = pixelOffsetY > 0 ? canvasHeight : -pixelOffsetY;
    
    const xStart = pixelOffsetX > 0 ? 0 : (pixelOffsetX < 0 ? -pixelOffsetX : 0);
    const xEnd = pixelOffsetX > 0 ? canvasWidth - pixelOffsetX : canvasWidth;
    
    for (let y = stripStartY; y < stripEndY; y++) {
      const y0 = yBase + y * yScale;
      const rowOffset = y * canvasWidth;
      
      for (let x = xStart; x < xEnd; x++) {
        const x0 = xBase + x * xScale;
        iterateUntilEscape(x0, y0, result);
        
        if (result.iterations !== MAX_ITERATIONS) {
          const mu = getMu(result.iterations, result.distanceSquared);
          cInput.numIterations = result.iterations;
          cInput.mu = mu;
          const [r, g, b, a] = colorFunc(cInput);
          const pr = r <= 0 ? 0 : r >= 255 ? 255 : r | 0;
          const pg = g <= 0 ? 0 : g >= 255 ? 255 : g | 0;
          const pb = b <= 0 ? 0 : b >= 255 ? 255 : b | 0;
          const pa = a <= 0 ? 0 : a >= 255 ? 255 : a | 0;
          newPixels32[rowOffset + x] = pr | (pg << 8) | (pb << 16) | (pa << 24);
        }
      }
    }
  }
  
  context.putImageData(newImageData, 0, 0);
  return true;
}

